import { useState } from "react";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { num } from "@/lib/format";
import api from "@/lib/api";
import { Bot, Sparkles, Send, TrendingUp, Loader2 } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const SUGGESTIONS = [
  "What is our Kraft paper safety-stock risk this month?",
  "Which products are driving the most demand right now?",
  "Summarise open production and quality risks for the plant.",
  "Where should we focus procurement spend next quarter?",
];

const COMPLIANCE_PROMPTS = [
  "EUDR high-risk?",
  "Nevada tax due?",
  "France e-invoicing status?",
];

export default function Copilot() {
  const { activeSite, current } = useSite();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [fcBusy, setFcBusy] = useState(false);

  const ask = async (q) => {
    const question = q || input;
    if (!question.trim()) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/copilot", { question, site: activeSite });
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "The AI service is unavailable right now. Please try again." }]);
    } finally { setBusy(false); }
  };

  const runForecast = async () => {
    setFcBusy(true);
    try {
      const { data } = await api.get(`/ai/forecast?site=${activeSite}`);
      setForecast(data);
    } catch (e) { setForecast({ demand: [], insight: "Forecast unavailable right now." }); }
    finally { setFcBusy(false); }
  };

  return (
    <div>
      <PageHeader title="AI Copilot" subtitle={`Natural-language operations intelligence powered by Gemini 3.1 Pro · scope: ${current.name}`} icon={Bot} accent="#A855F7" />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Chat */}
        <div className="lg:col-span-2 flex flex-col rounded-xl border border-[#e5e9f0] bg-[#ffffff]" style={{ minHeight: 520 }}>
          <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="copilot-messages">
            {messages.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center text-center text-[#6a6d70]">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#A855F7]/15 text-[#A855F7]"><Sparkles size={28} /></div>
                <p className="mt-4 max-w-sm text-sm">Ask about stock, demand, production, quality or finance across your sites. Try one:</p>
                <div className="mt-4 grid gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid={`copilot-suggestion`} onClick={() => ask(s)}
                      className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-left text-xs text-[#454b52] hover:border-[#A855F7]">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-4 py-2.5 text-sm ${
                  m.role === "user" ? "bg-[#A855F7] text-white" : "border border-[#e5e9f0] bg-[#eef1f5] text-[#32363a]"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-[#6a6d70] text-sm"><Loader2 className="animate-spin" size={16} /> Copilot is analysing…</div>}
          </div>
          <div className="border-t border-[#e5e9f0] p-3">
            <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="copilot-compliance-prompts">
              <span className="self-center text-[10px] font-semibold uppercase tracking-wide text-[#89919a]">Compliance:</span>
              {COMPLIANCE_PROMPTS.map((p, i) => (
                <button key={p} type="button" data-testid={`copilot-prompt-${i}`} onClick={() => ask(p)} disabled={busy}
                  className="rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-[11px] font-medium text-[#A855F7] transition hover:bg-[#A855F7]/20 disabled:opacity-50">{p}</button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
              <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the ERP copilot…"
                className="flex-1 rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-4 py-2.5 text-sm text-[#32363a] outline-none focus:border-[#A855F7]" />
              <button data-testid="copilot-send" type="submit" disabled={busy || !input.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#A855F7] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9333ea] disabled:opacity-50">
                <Send size={16} /> Ask</button>
            </form>
          </div>
        </div>

        {/* Forecast */}
        <div className="rounded-xl border border-[#e5e9f0] bg-[#ffffff] p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#32363a]">Demand Forecast</h3>
            <TrendingUp size={18} className="text-[#A855F7]" />
          </div>
          <p className="mt-1 text-xs text-[#6a6d70]">Next-quarter outlook & raw-material advice.</p>
          <button data-testid="run-forecast-btn" onClick={runForecast} disabled={fcBusy}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-[#A855F7]/15 px-4 py-2 text-sm font-semibold text-[#A855F7] hover:bg-[#A855F7]/25 disabled:opacity-50">
            {fcBusy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Generate Forecast</button>

          {forecast && (
            <div className="mt-4" data-testid="ai-demand-forecast-chart">
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={forecast.demand} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
                    <XAxis type="number" stroke="#64748B" fontSize={10} tickFormatter={(v) => `${num(v / 1000)}k`} />
                    <YAxis type="category" dataKey="product" stroke="#64748B" fontSize={9} width={90} />
                    <Tooltip contentStyle={{ background: "#eef1f5", border: "1px solid #e5e9f0", borderRadius: 8, color: "#32363a" }} formatter={(v) => `${num(v)} pcs`} />
                    <Bar dataKey="qty" fill="#A855F7" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 whitespace-pre-wrap rounded-lg border border-[#e5e9f0] bg-[#eef1f5] p-3 text-xs text-[#454b52]">{forecast.insight}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
